/**
 * Thin better-auth-compatible client for `/api/v1/auth/*`. The app must
 * remain fully usable offline as a guest, so every method here fails soft:
 * network errors are surfaced as a distinguishable `offline` outcome rather
 * than thrown, letting callers fall back to local/guest mode instead of
 * blocking the UI.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export type AuthUser = { id: string; email: string; name?: string };

export class AuthApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new AuthApiError('offline', 0);
  }
  if (!res.ok) {
    let message = 'Er ging iets mis. Probeer het opnieuw.';
    try {
      const body = await res.json();
      message = body?.error?.message ?? body?.message ?? message;
    } catch {
      // Non-JSON error body — keep default message.
    }
    throw new AuthApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type SessionResult =
  { kind: 'authenticated'; user: AuthUser } | { kind: 'guest' } | { kind: 'offline' };

export type AuthActionResult = { ok: true } | { ok: false; offline: boolean; message: string };
export type AuthLoginResult =
  { ok: true; user: AuthUser } | { ok: false; offline: boolean; message: string };

function toActionResult(err: unknown): { ok: false; offline: boolean; message: string } {
  if (err instanceof AuthApiError) {
    return {
      ok: false,
      offline: err.status === 0,
      message: err.status === 0 ? 'auth.login.offlineNotice' : err.message,
    };
  }
  return { ok: false, offline: false, message: 'auth.errors.generic' };
}

export const authClient = {
  async getSession(): Promise<SessionResult> {
    try {
      const data = await request<{ user?: AuthUser } | null>('/auth/get-session', {
        method: 'GET',
      });
      return data?.user ? { kind: 'authenticated', user: data.user } : { kind: 'guest' };
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 0) return { kind: 'offline' };
      return { kind: 'guest' };
    }
  },

  async login(email: string, password: string): Promise<AuthLoginResult> {
    try {
      const data = await request<{ user: AuthUser }>('/auth/sign-in/email', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      return { ok: true, user: data.user };
    } catch (err) {
      return toActionResult(err);
    }
  },

  async register(name: string, email: string, password: string): Promise<AuthLoginResult> {
    try {
      const data = await request<{ user: AuthUser }>('/auth/sign-up/email', {
        method: 'POST',
        body: JSON.stringify({ name, email, password }),
      });
      return { ok: true, user: data.user };
    } catch (err) {
      return toActionResult(err);
    }
  },

  async logout(): Promise<void> {
    try {
      await request<void>('/auth/sign-out', { method: 'POST' });
    } catch {
      // Ignore — client-side session state is cleared regardless.
    }
  },

  async requestPasswordReset(email: string): Promise<AuthActionResult> {
    try {
      await request<void>('/auth/forget-password', {
        method: 'POST',
        body: JSON.stringify({ email, redirectTo: '/wachtwoord-herstellen' }),
      });
      return { ok: true };
    } catch (err) {
      return toActionResult(err);
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<AuthActionResult> {
    try {
      await request<void>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
      return { ok: true };
    } catch (err) {
      return toActionResult(err);
    }
  },
};
